import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import classNames from 'classnames';

import { withStyles } from '@material-ui/core/styles';
import ListItem from '@material-ui/core/ListItem';
import ListItemSecondaryAction from '@material-ui/core/ListItemSecondaryAction';
import Typography from '@material-ui/core/Typography';

import IconButton from 'components/common/IconButton';
import Menu, { MenuItem } from 'components/common/Menu';
import Avatar from 'components/common/Avatar';
import DeleteMenu from 'components/common/DeleteMenu';
import IconMoreVert from 'components/icons/IconMoreVert';
import Link from 'components/common/Link';
import Chip from 'components/common/Chip';
import WorkflowState from 'components/common/WorkflowState';

import formatUtcToLocalDate from 'utils/formatUtcToLocalDate';

import { deletePatientNote } from 'redux/modules/patientsNotes';
import { deleteOrderNote } from 'redux/modules/ordersNotes';
import { addNotification } from 'redux/modules/notifications';

import dateFormat from 'config/dateFormat';
import timeDateFormat from 'config/timeDateFormat';
import pxToEm from 'styles/pxToEm';

import splitLongWords from 'utils/splitLongWords';

import NotesForm from './NotesForm';

const styles = theme => ({
  root: {
    padding: '8px 0',
    alignItems: 'flex-start',
    '& + $root': {
      borderTop: `1px solid ${theme.palette.primary.A100}`,
    },
  },
  headerPrimaryText: {
    display: 'flex',
    paddingRight: 36,
    marginBottom: 4,
    justifyContent: 'space-between',
  },
  headerSecondaryText: {
    display: 'flex',
    flexWrap: 'wrap',
    marginBottom: 4,
  },
  avatar: {
    width: 38,
    height: 38,
    marginLeft: 15,
  },
  body: {
    width: '100%',
    paddingLeft: 12,
    paddingTop: 3,
  },
  listItemSecondaryAction: {
    top: 6,
    margin: '0 -15px 0 0',
    transform: 'none',
  },
  button: {
    width: 42,
    height: 42,
  },
  typographyCaption: {
    lineHeight: pxToEm(22, 12),
    opacity: '0.5',
  },
  chipCategory: {
    marginRight: 4,
    marginBottom: 4,
  },
  iconMoreVert: {
    width: 24,
    height: 24,
  },
  author: {
    marginRight: 16,
  },
  orderLink: {
    lineHeight: pxToEm(24, 12),
    marginBottom: 4,
    marginRight: 16,
  },
  state: {
    top: 16,
    height: 38,
  },
});

class NotesItem extends Component {
  constructor(props) {
    super(props);

    this.state = {
      edit: false,
      anchorEl: null,
      deleteMenuOpen: false,
    };

    this.onMenuButtonClick = this.onMenuButtonClick.bind(this);
    this.onEditMenuItemClick = this.onEditMenuItemClick.bind(this);
    this.onCancelButtonClick = this.onCancelButtonClick.bind(this);
    this.onSubmitSuccess = this.onSubmitSuccess.bind(this);
    this.onMenuClose = this.onMenuClose.bind(this);
    this.onDeleteMenuItemClick = this.onDeleteMenuItemClick.bind(this);
    this.onDeleteCancel = this.onDeleteCancel.bind(this);
    this.onDeleteSubmit = this.onDeleteSubmit.bind(this);
    this.onOrderLinkClick = this.onOrderLinkClick.bind(this);
  }

  onMenuButtonClick(event) {
    this.setState({
      anchorEl: event.currentTarget,
      deleteMenuOpen: false,
    });
  }

  onEditMenuItemClick() {
    this.onMenuClose();
    this.setState({ edit: true });
  }

  onCancelButtonClick() {
    this.setState({ edit: false });
  }

  onSubmitSuccess(props) {
    this.setState({ edit: false });
    this.props.onSubmitSuccess(props);
  }

  onMenuClose() {
    this.setState({ anchorEl: null });
  }

  onDeleteMenuItemClick(event) {
    this.setState({ deleteMenuOpen: true });
  }

  onDeleteCancel() {
    this.setState({ anchorEl: null });
  }

  onDeleteSubmit() {
    const { actions, ID } = this.props;
    // eslint-disable-next-line camelcase
    if (this.props['Order ID']) {
      actions.deleteOrderNote({ ID });
    } else {
      actions.deletePatientNote({ ID });
    }
    this.setState({
      deleteMenuOpen: false,
      anchorEl: null,
    });
  }

  onOrderLinkClick() {
    const { actions } = this.props;
    if (this.props['Order Is Delete']) {
      actions.addNotification({
        message: 'Failed to open - order was deleted',
        variant: 'warning',
      });
    } else {
      /* eslint-disable camelcase */
      window.open(this.props['Is Archive']
        ? `/workflow/archive_order/view/${this.props['Order ID']}`
        : `/workflow/order/view/${this.props['Order ID']}`, '_blank');
      /* eslint-enable camelcase */
    }
  }

  render() {
    const { ID, Text, Author, account, business, disabled, classes } = this.props;
    const { edit, anchorEl } = this.state;
    const open = Boolean(anchorEl);
    let avatarImgSrc = (Author.Image && Object.keys(Author.Image).length > 0)
      ? `${Author.Image['File Url']}${Author.Image['File Name']}`
      : null;
    if (avatarImgSrc && process.env.REACT_APP_API_ROOT) {
      avatarImgSrc = `${process.env.REACT_APP_API_ROOT}${avatarImgSrc}`;
    }
    const workflowState = this.props['Notes Category'].reduce((accumulator, currentValue) => ([
      ...accumulator,
      ...currentValue['Workflow State'] && currentValue['Workflow State'].ID === 1 && accumulator.indexOf(1) === -1 ? [1] : [],
      ...currentValue['Workflow State'] && currentValue['Workflow State'].ID === 2 && accumulator.indexOf(2) === -1 ? [2] : [],
      ...currentValue['Workflow State'] && currentValue['Workflow State'].ID === 3 && accumulator.indexOf(3) === -1 ? [3] : [],
      ...currentValue['Workflow State'] && currentValue['Workflow State'].ID === 4 && accumulator.indexOf(4) === -1 ? [4] : [],
      ...currentValue['Workflow State'] && currentValue['Workflow State'].ID === 5 && accumulator.indexOf(5) === -1 ? [5] : [],
      ...!currentValue['Workflow State'].ID && accumulator.indexOf(null) === -1 ? [null] : [],
    ]), []).sort((a, b) => a - b);
    const tooltipContent = workflowState.length > 0 ? workflowState.map((item) => {
      if (item === 1) {
        return 'Intake'
      }
      if (item === 2) {
        return 'Delivery'
      }
      if (item === 3) {
        return 'Billing'
      }
      if (item === 4) {
        return 'Collection'
      }
      if (item === 5) {
        return 'Paid'
      }
      if (item === null) {
        return 'General'
      }
    }).join(', ') : 'General';
    return (
      <ListItem className={classes.root}>
        <WorkflowState
          className={classes.state}
          workflowState={workflowState}
          tooltipContent={tooltipContent}
        />
        <Avatar
          className={classes.avatar}
          src={avatarImgSrc}
        />
        <div className={classes.body}>
          <div className={classes.headerPrimaryText}>
            <Typography
              className={classes.author}
            >
              {splitLongWords(`${Author['First Name']} ${Author['Last Name']}`)}
            </Typography>
            <Typography
              className={classes.typographyCaption}
              color="secondary"
              component="time"
              variant="caption"
              dateTime={formatUtcToLocalDate(
                this.props['Create At'],
                timeDateFormat,
                dateFormat,
                business['Company Address']['Time Zone'],
                business['Company Address']['Observes Daylight Saving'])}
              /* eslint-disable camelcase */
            >
              {formatUtcToLocalDate(
                this.props['Create At'],
                timeDateFormat,
                timeDateFormat,
                business['Company Address']['Time Zone'],
                business['Company Address']['Observes Daylight Saving'])}
            </Typography>
          </div>
          <div className={classes.headerSecondaryText}>
            {(this.props['Order ID'] && this.props['HCPC S Codes']) &&
              <Fragment>
                <Link
                  className={classes.orderLink}
                  color="primary"
                  size="sm"
                  bold
                  onClick={this.onOrderLinkClick}
                >
                  {`Order ${this.props['Order ID']}: `}
                  {this.props['HCPC S Codes'].slice(0, 2).join(', ')}
                  {this.props['HCPC S Codes'].length > 2 ? '...' : ''}
                </Link>
              </Fragment>
            }
            {edit === false && this.props['Notes Category'].length > 0 &&
              <Fragment>
                {this.props['Notes Category'].map(item => (
                  <Chip
                    key={item.ID || item.Name}
                    className={classes.chipCategory}
                    label={item.Name}
                    size="sm"
                    square
                  />
                ))}
              </Fragment>
            }
          </div>
          {edit === false &&
            <Fragment>
              <ListItemSecondaryAction className={classes.listItemSecondaryAction}>
                <IconButton
                  aria-label="More"
                  aria-owns={open ? `${ID}-note-menu` : null}
                  aria-haspopup="true"
                  onClick={this.onMenuButtonClick}
                  tooltipContent="Open Menu"
                >
                  <IconMoreVert
                    className={classes.iconMoreVert}
                  />
                </IconButton>
                <Menu
                  id={`${ID}-note-menu`}
                  anchorEl={this.state.anchorEl}
                  open={open}
                  onClose={this.onMenuClose}
                  list={!this.state.deleteMenuOpen}
                >
                  {this.state.deleteMenuOpen ? (
                    <DeleteMenu
                      text="Are you sure you want to delete this note?"
                      onCancel={this.onDeleteCancel}
                      onSubmit={this.onDeleteSubmit}
                    />
                  ) : (
                    <Fragment>
                      <MenuItem
                        onClick={this.onEditMenuItemClick}
                        disabled={disabled || this.props['Is Archive'] || (account.ID !== Author.ID
                          && account.Roles.filter(item =>
                            item.Name === 'Master Administrator' || item.Name === 'Administrator').length === 0)}
                      >
                        Edit
                      </MenuItem>
                      <MenuItem
                        color="warning"
                        onClick={this.onDeleteMenuItemClick}
                        disabled={disabled || this.props['Is Archive'] || account.Roles.filter(item =>
                          item.Name === 'Master Administrator' || item.Name === 'Administrator' || item.Name === 'Manager').length === 0}
                      >
                        Delete
                      </MenuItem>
                    </Fragment>
                  )}
                </Menu>
              </ListItemSecondaryAction>
            </Fragment>
          }
          {(edit === true) ? (
            <NotesForm
              form={`patientNoteEdit-${ID}`}
              initialValues={{
                ID,
                Text,
                'Notes Category': this.props['Notes Category'].map(item => ({
                  label: item.Name,
                  value: item.ID,
                })),
              }}
              onCancelButtonClick={this.onCancelButtonClick}
              onSubmitSuccess={this.onSubmitSuccess}
              // eslint-disable-next-line camelcase
              variant={this.props['Order ID'] ? 'order' : 'patient'}
            />
          ) : (
            <div>
              <Typography color="secondary">
                {Text}
              </Typography>
            </div>
          )}
        </div>
      </ListItem>
    );
  }
}

NotesItem.propTypes = {
  Text: PropTypes.string.isRequired,
  ID: PropTypes.number.isRequired,
  'Create At': PropTypes.string.isRequired,
  'Order ID': PropTypes.number,
  'HCPC S Codes': PropTypes.arrayOf(PropTypes.string),
  Author: PropTypes.shape({
    'First Name': PropTypes.string.isRequired,
    'Last Name': PropTypes.string.isRequired,
    ID: PropTypes.number.isRequired,
    Image: PropTypes.shape({
      'File Url': PropTypes.string,
      'File Name': PropTypes.string,
    }),
  }).isRequired,
  'Notes Category': PropTypes.arrayOf(PropTypes.shape({
    Name: PropTypes.string,
  })),
  'Is Archive': PropTypes.bool,
  actions: PropTypes.shape({
    deletePatientNote: PropTypes.func.isRequired,
    deleteOrderNote: PropTypes.func.isRequired,
    addNotification: PropTypes.func.isRequired,
  }).isRequired,
  account: PropTypes.shape({
    Roles: PropTypes.arrayOf(PropTypes.shape({
      Name: PropTypes.string.isRequired,
    })).isRequired,
  }).isRequired,
  business: PropTypes.shape({
    ID: PropTypes.number,
    'Time Zone': PropTypes.string,
  }),
  'Order Is Delete': PropTypes.bool,
  disabled: PropTypes.bool,
  classes: PropTypes.shape({
    root: PropTypes.string.isRequired,
    body: PropTypes.string.isRequired,
    listItemSecondaryAction: PropTypes.string.isRequired,
    button: PropTypes.string.isRequired,
    typographyCaption: PropTypes.string.isRequired,
    author: PropTypes.string.isRequired,
    chipCategory: PropTypes.string.isRequired,
  }).isRequired,
};

NotesItem.defaultProps = {
  'Order ID': null,
  business: {},
  'Order Is Delete': false,
  'Notes Category': [],
  disabled: false,
  'Is Archive': false,
};

const mapStateToProps = store => ({
  account: store.account,
});

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators({
    deletePatientNote, deleteOrderNote, addNotification,
  }, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(
  withStyles(styles)(NotesItem),
);



// WEBPACK FOOTER //
// ./src/components/patient/PatientProfile/Notes/NotesItem.js